import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output
} from '@angular/core';
import { AppTranslationService } from '@service/translation/app-translation.service';
// Importaciones modulares individuales para mejor tree-shaking
import { SuraIconComponent, IconConfig } from '@sura-ui/icon';
import { SuraImageComponent, ImageConfig } from '@sura-ui/image';
import { RouteService } from '@helpers/routing/router.service';
import { APP_VERSION } from 'src/app/common/constants-ejb';

/**
 * Componente que representa la barra superior de la aplicación.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-top-bar',
  imports: [SuraIconComponent, SuraImageComponent],
  template: `
    <div
      class="h-14 w-full bg-white dark:bg-gray-900 shadow-sm text-center border-b border-gray-100 dark:border-gray-700 transition-colors duration-300"
    >
      <div class="flex h-full items-center justify-between">
        <div class="flex items-center">
          <div
            tabindex="0"
            class="flex h-14 w-14 cursor-pointer items-center justify-center hover:bg-sura-blue-50 dark:hover:bg-gray-800 transition-colors duration-200"
            (click)="onMenuClicked()"
            (keyup.enter)="onMenuClicked()"
          >
            <sura-icon
              [config]="getMenuIconConfig()"
              class="text-sura-blue-500 dark:text-sura-blue-300"
            >
            </sura-icon>
          </div>
          <div class="flex items-center gap-3">
            <button (click)="navigate('/dashboard')">
              <sura-image
                class="girop-logo-tint"
                [config]="getLogoImageConfig()"
              ></sura-image>
            </button>
            <div class="bg-sura-blue-50 dark:bg-gray-700 h-10 w-px"></div>
            <span
              class="text-xl font-bold text-sura-blue-800 dark:text-sura-blue-200 ml-2"
              >Girop
              <span class="text-xs font-normal text-gray-400 dark:text-gray-500"
                >v{{ appVersion }}</span
              ></span
            >
          </div>
        </div>

        <div class="flex items-center gap-2 pr-2 sm:pr-4 min-w-0">
          <div
            tabindex="1"
            class="flex h-10 cursor-pointer items-center gap-2 px-2 sm:px-3 rounded-full bg-sura-blue-50 dark:bg-gray-800 hover:bg-sura-blue-200 dark:hover:bg-gray-700 transition-colors duration-200 max-w-[180px] sm:max-w-[240px] md:max-w-none"
            [title]="username()"
            (click)="onUserMenuClicked()"
            (keyup.enter)="onUserMenuClicked()"
          >
            <sura-icon
              [config]="{ name: 'user', size: 'medium', color: '#2d6df6' }"
            ></sura-icon>
            <!-- En pantallas muy chicas (xs) ocultamos el nombre para evitar wrap -->
            <span
              class="hidden sm:inline text-sm font-medium text-sura-blue-800 dark:text-gray-200 truncate whitespace-nowrap"
              >{{ username() }}</span
            >
            <sura-icon
              [config]="{
                name: 'ellipsis-v',
                size: 'small',
                color: '#2d6df6',
                clickable: true
              }"
            ></sura-icon>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: ``
})
export class TopBarComponent {
  appVersion = APP_VERSION;

  /** Nombre del usuario */
  username = input.required<string>();

  /** Último acceso del usuario */
  lastAccess = input.required<string>();

  /** Servicio de traducción de la aplicación */
  private translatorService = inject(AppTranslationService);
  router: RouteService = inject(RouteService);
  /**
   * Emite evento cuando el menú de opciones cambia de estado.
   */
  @Output() menuStateChange = new EventEmitter<string>();

  /**
   * Emite evento cuando el menú de usuario cambia de estado.
   */
  @Output() userMenuStateChange = new EventEmitter<string>();

  /** Estado del menú de opciones y usuario */
  private optionMenuState = 'closed';
  private userMenuState = 'closed';

  getLastConnection = computed(
    (): string =>
      `${this.translatorService.get('security.last-access', { lastAccess: this.lastAccess() })}`
  );

  navigate(url?: string) {
    this.router.navigateTo(url ?? '/');
  }

  onMenuClicked() {
    this.optionMenuState =
      this.optionMenuState === 'closed' ? 'opened' : 'closed';
    this.menuStateChange.emit(this.optionMenuState);
  }

  /**
   * Cambia el estado del menú de usuario.
   */
  onUserMenuClicked() {
    this.userMenuState = this.userMenuState === 'closed' ? 'opened' : 'closed';
    this.userMenuStateChange.emit(this.userMenuState);
  }

  /**
   * Configuración del icono del menú.
   */
  getMenuIconConfig(): IconConfig {
    return {
      name: 'menu',
      size: 'large',
      color: '#2d6df6',
      clickable: true
    };
  }

  /**
   * Configuración del logo de la aplicación.
   */
  getLogoImageConfig(): ImageConfig {
    return {
      src: 'assets/images/girop-logo.png',
      alt: 'logo',
      width: 83,
      height: 28
    };
  }
}
